<template>
  <div class="settings-page admin-page">
    <header class="admin-page__header">
      <div class="admin-page__title">
        <h2>系统设置</h2>
        <p>支付汇率、域名服务商接入与业务参数</p>
      </div>
    </header>

    <a-card :bordered="false" class="admin-card settings-card">
      <div class="settings-section">
        <div class="settings-section__head">
          <h3>支付 / 汇率</h3>
          <p>UPay 支付通道与人民币结算汇率</p>
        </div>
        <a-form layout="horizontal" :label-col="{ span: 6 }" :wrapper-col="{ span: 14 }">
          <a-form-item label="UPay endpoint">
            <a-input v-model="kv['upay.endpoint']" placeholder="https://..." />
          </a-form-item>
          <a-form-item label="UPay key">
            <a-input-password
              v-model="kv['upay.key']"
              placeholder="加密存储, 留空不修改"
            />
          </a-form-item>
          <a-form-item label="CNY 汇率">
            <a-input v-model="kv['upay.cny_rate']" />
          </a-form-item>
        </a-form>
      </div>

      <a-divider />

      <div class="settings-section">
        <div class="settings-section__head">
          <h3>域名服务商</h3>
          <p>NameSilo 注册商 + Cloudflare DNS 接入凭证</p>
        </div>
        <a-form layout="horizontal" :label-col="{ span: 6 }" :wrapper-col="{ span: 14 }">
          <a-form-item label="NameSilo API key">
            <a-input-password v-model="kv['namesilo.api_key']" placeholder="留空保留原值" />
          </a-form-item>
          <a-form-item label="NameSilo sandbox">
            <a-input v-model="kv['namesilo.sandbox']" placeholder="1 = sandbox, 0 = prod" />
          </a-form-item>
          <a-form-item label="Cloudflare API token">
            <a-input-password v-model="kv['cf.api_token']" placeholder="留空保留原值" />
          </a-form-item>
          <a-form-item label="Cloudflare account_id">
            <a-input v-model="kv['cf.account_id']" />
          </a-form-item>
          <a-form-item label="宝塔面板 URL">
            <a-input v-model="kv['bt.panel_url']" placeholder="https://your-server:8888 （末尾不带 /）" />
          </a-form-item>
          <a-form-item label="宝塔 API key">
            <a-input-password v-model="kv['bt.api_key']" placeholder="加密存储, 留空保留原值（宝塔→设置→API 接口）" />
          </a-form-item>
        </a-form>
      </div>

      <a-divider />

      <div class="settings-section">
        <div class="settings-section__head">
          <h3>域名健康检测</h3>
          <p>VirusTotal API（用于域名「健康度」面板的 VT 标红检查）</p>
        </div>
        <a-form layout="horizontal" :label-col="{ span: 6 }" :wrapper-col="{ span: 14 }">
          <a-form-item label="VirusTotal API key">
            <a-input-password v-model="kv['vt.api_key']" placeholder="加密存储, 留空保留原值；为空时 VT 项跳过不报错" />
          </a-form-item>
        </a-form>
      </div>

      <a-divider />

      <div class="settings-section">
        <div class="settings-section__head">
          <h3>业务参数</h3>
          <p>BYO 额度、充值预设与默认注册年限</p>
        </div>
        <a-form layout="horizontal" :label-col="{ span: 6 }" :wrapper-col="{ span: 14 }">
          <a-form-item label="BYO 额外额度单价 (U)">
            <a-input v-model="kv['byo.extra_slot_price_u']" />
          </a-form-item>
          <a-form-item label="BYO 单次最多购买数">
            <a-input v-model="kv['byo.max_buy_per_order']" />
          </a-form-item>
          <a-form-item label="充值预设金额">
            <a-input
              v-model="kv['recharge.preset_amounts']"
              placeholder="逗号分隔, 如 10,50,100"
            />
          </a-form-item>
          <a-form-item label="充值最小金额 (U)">
            <a-input v-model="kv['recharge.min_u']" />
          </a-form-item>
          <a-form-item label="默认域名注册年限">
            <a-input v-model="kv['default_domain_years']" />
          </a-form-item>
        </a-form>
      </div>
    </a-card>

    <a-affix :offset-bottom="0">
      <div class="settings-savebar">
        <span class="settings-savebar__hint">
          <a-icon type="info-circle" />
          带星号字段为加密存储,留空表示保持原值不变
        </span>
        <a-button type="primary" size="large" @click="onSave" :loading="saving">
          保存所有改动
        </a-button>
      </div>
    </a-affix>
  </div>
</template>

<script>
import { listSettings, saveSettings } from '@/api/settings';

const KEYS = [
  'upay.endpoint',
  'upay.key',
  'upay.cny_rate',
  'namesilo.api_key',
  'namesilo.sandbox',
  'cf.api_token',
  'cf.account_id',
  'bt.panel_url',
  'bt.api_key',
  'vt.api_key',
  'byo.extra_slot_price_u',
  'byo.max_buy_per_order',
  'recharge.preset_amounts',
  'recharge.min_u',
  'default_domain_years',
];

const ENCRYPTED_KEYS = ['upay.key', 'namesilo.api_key', 'cf.api_token', 'bt.api_key', 'vt.api_key'];

function emptyKv() {
  const o = {};
  KEYS.forEach(k => (o[k] = ''));
  return o;
}

export default {
  data() {
    return {
      saving: false,
      kv: emptyKv(),
    };
  },
  mounted() {
    listSettings()
      .then(res => {
        (res.data || []).forEach(r => {
          if (KEYS.includes(r.k)) {
            this.kv[r.k] = ENCRYPTED_KEYS.includes(r.k) ? '' : (r.v == null ? '' : r.v);
          }
        });
      })
      .catch(e => this.$message.error((e && e.data && e.data.message) || '加载失败'));
  },
  methods: {
    onSave() {
      this.saving = true;
      const items = Object.entries(this.kv)
        .filter(([k, v]) => {
          if (ENCRYPTED_KEYS.includes(k) && (v === '' || v == null)) return false;
          return true;
        })
        .map(([k, v]) => ({ k, v }));
      saveSettings(items)
        .then(() => this.$message.success('保存成功'))
        .catch(e => this.$message.error((e && e.data && e.data.message) || '保存失败'))
        .finally(() => (this.saving = false));
    },
  },
};
</script>
</content>
</invoke>